import React, { useState, useCallback, useMemo } from 'react';
import Modal from './Modal';

const DEFAULT_STATUS_OPTIONS = [
  { value: 'To-Do', label: 'To-Do' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Deferred', label: 'Deferred' },
  { value: 'Blocked', label: 'Blocked' },
];

const DEFAULT_PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Urgent', label: 'Urgent' },
];

const BLANK = { title: '', description: '', due_date: '', status: 'To-Do', priority: 'Low', assigned_to: '' };

/**
 * Standalone "New Task" form, extracted from TaskManagement.js's "Add New
 * Task" modal so it can be embedded inline (locked to one case) instead of
 * only reachable from the global Task Management page.
 */
export default function NewTaskModal({
  open,
  onClose,
  onCreated,
  authAxios,
  showFormMessage,
  caseId,
  users = [],
  paralegalsList = [],
  userRole,
  currentUser,
  taskStatusOptions = DEFAULT_STATUS_OPTIONS,
  taskPriorityOptions = DEFAULT_PRIORITY_OPTIONS,
}) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const allowedUsers = useMemo(() => {
    const map = new Map();
    if (userRole === 'ASSOCIATE') {
      for (const u of (Array.isArray(users) ? users : [])) map.set(u.id, u);
      for (const u of (Array.isArray(paralegalsList) ? paralegalsList : [])) map.set(u.id, u);
      if (currentUser?.id) map.set(currentUser.id, { id: currentUser.id, username: currentUser.username || 'You' });
      return Array.from(map.values());
    }
    for (const u of (Array.isArray(users) ? users : [])) map.set(u.id, u);
    return Array.from(map.values());
  }, [users, paralegalsList, userRole, currentUser]);

  const fld = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authAxios.post('tasks/', { ...form, case: caseId, assigned_to: form.assigned_to || null });
      showFormMessage?.('success', 'Task added successfully!');
      setForm(BLANK);
      onCreated?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data ? JSON.stringify(err.response.data) : (err?.message || 'Failed to add task.');
      showFormMessage?.('danger', `Failed to add task. ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [authAxios, form, caseId, showFormMessage, onCreated, onClose]);

  return (
    <Modal open={open} onClose={onClose} title="New Task" maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Title</label>
          <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
            name="title" value={form.title} onChange={fld} required autoFocus />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Due Date & Time</label>
            <input type="datetime-local" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="due_date" value={form.due_date} onChange={fld} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Assigned To</label>
            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="assigned_to" value={form.assigned_to} onChange={fld}>
              <option value="">Unassigned</option>
              {allowedUsers.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="status" value={form.status} onChange={fld}>
              {taskStatusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Priority</label>
            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="priority" value={form.priority} onChange={fld}>
              {taskPriorityOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm" rows={3}
            name="description" value={form.description} onChange={fld} />
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
            {saving ? 'Adding…' : 'Add Task'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
